// =============================================================================
// sound.ts — Web Audio synthesised UI sounds + a `useSound` React hook.
//
// Why this exists:
//   We want subtle audio accents on button taps, RSVP confirmation, music
//   toggles, etc. We don't want to ship audio asset files for kilobyte-sized
//   blips. So every sound here is synthesised on demand with the Web Audio
//   API — a tiny oscillator (or filtered noise burst) shaped by an ADSR-ish
//   gain envelope. Total cost: a few hundred bytes per sound at runtime, no
//   network, no preload.
//
// Usage:
//   import { useSound } from "@/lib/sound";
//   const { play, enabled, setEnabled, toggle } = useSound();
//   <button onClick={() => play("click")}>Click me</button>
//
// Behaviour:
//   * AudioContext is lazily created on the first user gesture (browsers block
//     contexts created at module load).
//   * `enabled` is persisted to localStorage under "taklifla.sound" (default
//     true). A user with `prefers-reduced-motion: reduce` is auto-disabled
//     unless they have explicitly toggled before.
//   * Master volume is capped at 0.15 — these are accents, not stingers.
//   * `play(name)` is a no-op if disabled or if the audio context can't start.
// =============================================================================

import { useCallback, useEffect, useState } from "react";

// ─── Sound catalog ───────────────────────────────────────────────────────────

export type SoundName =
  | "click"
  | "tap"
  | "hover"
  | "pop"
  | "swoosh"
  | "chime"
  | "ding"
  | "music-on"
  | "music-off";

const STORAGE_KEY = "taklifla.sound";
const MASTER_VOLUME = 0.15;

// ─── Lazy AudioContext ───────────────────────────────────────────────────────

type AudioCtxCtor = typeof AudioContext;
let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (ctx) return ctx;
  const Ctor: AudioCtxCtor | undefined =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: AudioCtxCtor }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
    return ctx;
  } catch {
    return null;
  }
}

async function ensureRunning(c: AudioContext): Promise<boolean> {
  if (c.state === "suspended") {
    try {
      await c.resume();
    } catch {
      return false;
    }
  }
  return c.state === "running";
}

// ─── Synthesis primitives ────────────────────────────────────────────────────

function makeNoiseBuffer(c: AudioContext, durationSec: number): AudioBuffer {
  const sampleRate = c.sampleRate;
  const length = Math.max(1, Math.floor(durationSec * sampleRate));
  const buffer = c.createBuffer(1, length, sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  return buffer;
}

interface ToneOpts {
  type: OscillatorType;
  /** Start frequency in Hz. */
  freq: number;
  /** End frequency for an exponential sweep. Optional. */
  freqEnd?: number;
  /** Total duration in seconds. */
  duration: number;
  /** Peak gain (will be multiplied by master volume). */
  gain?: number;
  /** Attack time in seconds. */
  attack?: number;
  /** Start delay in seconds (relative to "now"). */
  startAt?: number;
}

function playTone(c: AudioContext, opts: ToneOpts) {
  const now = c.currentTime + (opts.startAt ?? 0);
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = opts.type;
  osc.frequency.setValueAtTime(opts.freq, now);
  if (opts.freqEnd !== undefined) {
    osc.frequency.exponentialRampToValueAtTime(
      Math.max(1, opts.freqEnd),
      now + opts.duration,
    );
  }

  const peak = (opts.gain ?? 1) * MASTER_VOLUME;
  const attack = opts.attack ?? 0.005;
  g.gain.setValueAtTime(0.0001, now);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak), now + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, now + opts.duration);

  osc.connect(g);
  g.connect(c.destination);
  osc.start(now);
  osc.stop(now + opts.duration + 0.02);
  osc.onended = () => {
    try {
      osc.disconnect();
      g.disconnect();
    } catch {
      /* noop */
    }
  };
}

function playNoiseBurst(
  c: AudioContext,
  durationSec: number,
  filterFreq: number,
  gain = 0.6,
) {
  const now = c.currentTime;
  const src = c.createBufferSource();
  src.buffer = makeNoiseBuffer(c, durationSec);
  const filter = c.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = filterFreq;
  const g = c.createGain();
  const peak = gain * MASTER_VOLUME;
  g.gain.setValueAtTime(0.0001, now);
  g.gain.exponentialRampToValueAtTime(peak, now + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, now + durationSec);

  src.connect(filter);
  filter.connect(g);
  g.connect(c.destination);
  src.start(now);
  src.stop(now + durationSec + 0.02);
  src.onended = () => {
    try {
      src.disconnect();
      filter.disconnect();
      g.disconnect();
    } catch {
      /* noop */
    }
  };
}

// ─── Per-sound recipes ───────────────────────────────────────────────────────

function renderSound(c: AudioContext, name: SoundName) {
  switch (name) {
    case "click":
      // 4kHz square, 30ms exp decay
      playTone(c, { type: "square", freq: 4000, duration: 0.03, gain: 0.35 });
      return;
    case "tap":
      // 800Hz sine, 40ms decay
      playTone(c, { type: "sine", freq: 800, duration: 0.04, gain: 0.5 });
      return;
    case "hover":
      // very quiet 1.2kHz triangle, 80ms
      playTone(c, { type: "triangle", freq: 1200, duration: 0.08, gain: 0.18 });
      return;
    case "pop":
      // 600 → 1200Hz sine sweep, 100ms
      playTone(c, {
        type: "sine",
        freq: 600,
        freqEnd: 1200,
        duration: 0.1,
        gain: 0.55,
      });
      return;
    case "swoosh":
      // filtered noise burst, 200ms
      playNoiseBurst(c, 0.2, 1400, 0.55);
      return;
    case "chime": {
      // C-E-G arpeggio: 523 + 659 + 784 Hz, 600ms total
      const notes = [523.25, 659.25, 783.99];
      notes.forEach((f, i) => {
        playTone(c, {
          type: "sine",
          freq: f,
          duration: 0.32,
          gain: 0.55,
          startAt: i * 0.12,
        });
      });
      return;
    }
    case "ding":
      // 880Hz sine, 200ms with a soft tail (longer decay simulates tail)
      playTone(c, { type: "sine", freq: 880, duration: 0.5, gain: 0.6 });
      return;
    case "music-on":
      // 440Hz then 660Hz, each 100ms
      playTone(c, { type: "sine", freq: 440, duration: 0.1, gain: 0.5 });
      playTone(c, {
        type: "sine",
        freq: 660,
        duration: 0.1,
        gain: 0.5,
        startAt: 0.1,
      });
      return;
    case "music-off":
      // reverse of music-on
      playTone(c, { type: "sine", freq: 660, duration: 0.1, gain: 0.5 });
      playTone(c, {
        type: "sine",
        freq: 440,
        duration: 0.1,
        gain: 0.5,
        startAt: 0.1,
      });
      return;
  }
}

// ─── Persistence ─────────────────────────────────────────────────────────────

function readStoredEnabled(): boolean {
  if (typeof window === "undefined") return true;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      // No stored preference. Auto-disable for users who prefer reduced motion.
      const reduce =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      return !reduce;
    }
    return raw === "1" || raw === "true";
  } catch {
    return true;
  }
}

function writeStoredEnabled(v: boolean) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, v ? "1" : "0");
  } catch {
    /* noop */
  }
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export interface UseSoundResult {
  play: (name: SoundName) => void;
  enabled: boolean;
  setEnabled: (v: boolean) => void;
  toggle: () => void;
}

export function useSound(): UseSoundResult {
  const [enabled, setEnabledState] = useState<boolean>(() => readStoredEnabled());

  useEffect(() => {
    writeStoredEnabled(enabled);
  }, [enabled]);

  const setEnabled = useCallback((v: boolean) => setEnabledState(v), []);
  const toggle = useCallback(() => setEnabledState((v) => !v), []);

  const play = useCallback(
    (name: SoundName) => {
      if (!enabled) return;
      const c = getCtx();
      if (!c) return;
      // Resume in the background; if it can't, we silently no-op.
      void ensureRunning(c).then((ok) => {
        if (!ok) return;
        try {
          renderSound(c, name);
        } catch {
          /* noop */
        }
      });
    },
    [enabled],
  );

  return { play, enabled, setEnabled, toggle };
}
